import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import { ActionIcon, Button } from '@mantine/core';
import { TbCheck, TbCopy, TbTool, TbX } from 'react-icons/tb';

import { troubleSheets } from './steps';
import { readParseSummary } from './parseSummary';
import { useReportHealth } from './reportHealth';
import { usePeriodRun } from './PeriodRunContext';
import { usePeriodById } from '../../../services/period.service';
import {
  useGetDetailedExtractionStatsByPeriodId,
  useGetFilesByPeriodId
} from '../../../services/funds.service';
import { useUserGroupsCurrentUserAdminState } from '../../../services/user.service';
import { formatElapsed } from '../../../utils/extractionProgress';
import { formatPeriodLabel } from '../../../utils/periodLabel';
import { ApproverDrawer } from '../../../pages/Project/Quarters/ApproverDrawer';
import { APPROVAL_STATUSES } from '../../../pages/Project/Quarters/data';
import { REPORT_TYPES } from '../../../consts/reportTypes';
import {
  customStyles,
  Sheet,
  Backdrop
} from '../../../pages/Project/ProjectListV2/ExtractionIssuesV2/styles';
import { Summary } from './style';

const EMPTY = [];

/* How long to wait for the report to count itself before summarising without it. */
const COUNT_WAIT_MS = 20_000;

/* What a person needs from a run they did not watch. */
const RunSummary = () => {
  const location = useLocation();
  const { outcome } = usePeriodRun();
  const health = useReportHealth();

  /* Held, rather than read straight off the outcome. */
  const [held, setHeld] = useState(null);
  /* Sending for approval is not a mutation but a choice of people. */
  const [approverOpen, setApproverOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  /* Inside the project it ran in, said the way the band says it. */
  const inThisWorkflow = outcome?.workflowId && location.pathname.includes(outcome.workflowId);

  useEffect(() => {
    if (outcome?.kind === 'done' && inThisWorkflow) setHeld(outcome);
  }, [outcome, inThisWorkflow]);

  /* The report is what this drawer is about, and it arrives after the run says it is done. */
  const [counted, setCounted] = useState(false);
  const [heldHealth, setHeldHealth] = useState(null);
  useEffect(() => {
    if (!held) setHeldHealth(null);
    else if (health) setHeldHealth(health);
  }, [held, health]);

  useEffect(() => {
    if (!held) {
      setCounted(false);
      return undefined;
    }
    if (heldHealth) {
      setCounted(true);
      return undefined;
    }
    const t = setTimeout(() => setCounted(true), COUNT_WAIT_MS);
    return () => clearTimeout(t);
  }, [held, heldHealth]);

  const shown = held && location.pathname.includes(held.workflowId) ? held : null;
  const periodId = shown?.periodId;
  const here = !!shown;
  /* `here` starts the queries; `counted` decides when there is something honest to show. */
  const open = here && counted;

  const { data: period } = usePeriodById({
    periodId,
    queryParams: { enabled: !!periodId && here }
  });
  const { data: files } = useGetFilesByPeriodId({
    periodId,
    queryParams: { enabled: !!periodId && here }
  });
  const { data: extractionRows } = useGetDetailedExtractionStatsByPeriodId({
    periodId,
    queryParams: { enabled: !!periodId && here }
  });
  const { data: currentUser } = useUserGroupsCurrentUserAdminState();

  const short = useMemo(() => troubleSheets(extractionRows) || EMPTY, [extractionRows]);

  const ran = shown?.startedAt && shown?.at ? formatElapsed(shown.at - shown.startedAt) : null;
  const fileNames = (Array.isArray(files) ? files : []).map(f => f?.name).filter(Boolean);
  /* Named the way the band names it, from the period itself — the outcome carries ids, not words. */
  const label = (period && formatPeriodLabel(period)) || 'This period';
  const stat = (key, names) => {
    const st = period?.[key] || {};
    const doneN = st[names.done] || 0;
    const total =
      doneN + names.short.reduce((sum, n) => sum + (st[n] || 0), 0) + (st[names.running] || 0);
    return { done: doneN, total };
  };
  const extraction = stat('extraction_stats', {
    done: 'successful_extractions_number',
    short: [
      'failed_extractions_number',
      'warned_extractions_number',
      'not_completed_extractions_number'
    ],
    running: 'in_progress_extractions_number'
  });
  /* The backend pluralises "in progress" one way for extractions and the other for… */
  const transformation = stat('transformation_stats', {
    done: 'successful_transformations_number',
    short: [
      'failed_transformations_number',
      'warned_transformations_number',
      'not_completed_transformations_number'
    ],
    running: 'in_progress_transformation_number'
  });
  /* Offered only where approval has not been set going yet. */
  const approvalUntouched =
    !period?.approve_progress_status ||
    period.approve_progress_status === APPROVAL_STATUSES.NOT_STARTED;
  // A conversion has no mapping, so the run carried none over.
  const mappingCarried = shown?.reportType !== REPORT_TYPES.CONVERSION;
  const funds = period?.fund_count || 0;
  const jurisdictions = period?.jurisdiction_count || 0;
  const criticals = heldHealth?.critical || 0;
  const warnings = heldHealth?.warning || 0;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  /* The parse is the only account of which funds made it in: 25 of 26 is not "report generated". */
  const parse = readParseSummary(shown?.parse, periodId);
  const parsedAifs = parse?.aifs || EMPTY;
  const convertedAifs = parse?.converted || EMPTY;
  const missedAifs = parse?.missed || EMPTY;
  const missedManager = parse?.missedManager || null;
  const named = row => `${row.fund_name}${row.reason ? ` (${row.reason})` : ''}`;

  const notes = [
    short.length ? `${plural(short.length, 'sheet')} came out short` : null,
    missedAifs.length ? `${plural(missedAifs.length, 'AIF')} not in the report` : null,
    missedManager ? 'the AIFM part did not convert' : null,
    criticals ? `${plural(criticals, 'critical issue')} to clear` : null
  ].filter(Boolean);

  if (!open) return null;

  const text = [
    `${label} ran end to end${ran ? ` in ${ran}` : ''}.`,
    fileNames.length ? `Built on: ${fileNames.join(', ')}.` : null,
    `${mappingCarried ? 'Mapping carried over from the previous period. ' : ''}${funds} AIFs and ${jurisdictions} jurisdictions copied.`,
    parse
      ? `${convertedAifs.length} of ${parsedAifs.length} AIFs converted from the workbook.`
      : null,
    missedAifs.length ? `Not in the report: ${missedAifs.map(named).join('; ')}.` : null,
    missedManager ? `AIFM: ${missedManager.reason || 'did not convert'}.` : null,
    short.length
      ? `Missing: ${short.map(s => `${s.sheet}${s.why ? ` (${s.why})` : ''}`).join('; ')}.`
      : 'Nothing came out short.',
    criticals ? `${plural(criticals, 'critical issue')} in the report, still to clear.` : null,
    warnings ? `${plural(warnings, 'warning')}.` : null,
    'Before submission: approval.'
  ]
    .filter(Boolean)
    .join('\n');

  const did = [
    fileNames.length || files?.length
      ? {
          n: `${fileNames.length || files.length}`,
          t: `file${(fileNames.length || files.length) === 1 ? '' : 's'} read`
        }
      : null,
    funds ? { n: `${funds}`, t: `AIF${funds === 1 ? '' : 's'} copied` } : null,
    jurisdictions
      ? { n: `${jurisdictions}`, t: `jurisdiction${jurisdictions === 1 ? '' : 's'} copied` }
      : null,
    mappingCarried ? { t: 'mapping carried over' } : null,
    extraction.total ? { n: `${extraction.done} of ${extraction.total}`, t: 'extracted' } : null,
    transformation.total
      ? { n: `${transformation.done} of ${transformation.total}`, t: 'transformed' }
      : null,
    parse
      ? {
          n: missedAifs.length
            ? `${convertedAifs.length} of ${parsedAifs.length}`
            : `${parsedAifs.length}`,
          t: `AIF${parsedAifs.length === 1 ? '' : 's'} converted`
        }
      : null,
    { t: 'report generated' }
  ].filter(Boolean);

  return (
    <Modal
      isOpen
      onRequestClose={() => setHeld(null)}
      style={customStyles}
      contentElement={(props, children) => <Sheet {...props}>{children}</Sheet>}
      overlayElement={(props, contentEl) => <Backdrop {...props}>{contentEl}</Backdrop>}
    >
      <Summary>
        <header>
          <span className="mid">
            <span className="tick" aria-hidden>
              <TbCheck size={13} strokeWidth={2.6} />
            </span>
            <span className="lines">
              <b>
                {label} ran end to end{ran ? ` in ${ran}` : ''}
              </b>
              <em>
                {[
                  currentUser?.full_name || currentUser?.name
                    ? `started by ${currentUser.full_name || currentUser.name}`
                    : null,
                  notes.length ? notes.join(' · ') : 'nothing came out short'
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </em>
            </span>
            <span className="acts">
              {/* Mantine's own, not the v2 ControlButton: the `--dx-*` tokens it is built on are
                  scoped to `.dx-v2`, and out here in the chrome they resolve to nothing at all —
                  which left the control painting as a line of bare text. */}
              <Button
                size="xs"
                variant="default"
                leftSection={copied ? <TbCheck size={14} /> : <TbCopy size={14} />}
                onClick={() => {
                  navigator.clipboard?.writeText(text);
                  setCopied(true);
                }}
              >
                {copied ? 'Copied' : 'Copy summary'}
              </Button>
              {approvalUntouched ? (
                <Button
                  size="xs"
                  variant={criticals ? 'default' : 'filled'}
                  onClick={() => setApproverOpen(true)}
                >
                  Send for approval
                </Button>
              ) : null}
              {/* The drawer named the issue bar and left them to find it. This goes there. */}
              {criticals && health?.startFixing ? (
                <Button
                  size="xs"
                  leftSection={<TbTool size={14} />}
                  onClick={() => {
                    setHeld(null);
                    health.startFixing();
                  }}
                >
                  Start fixing
                </Button>
              ) : null}
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Close"
                onClick={() => setHeld(null)}
              >
                <TbX size={16} />
              </ActionIcon>
            </span>
          </span>
        </header>

        <div className="mid body">
          <h4>What the run did</h4>
          <ul className="did">
            {did.map(item => (
              <li key={item.t}>
                <i aria-hidden>
                  <TbCheck size={12} strokeWidth={3} />
                </i>
                {item.n ? <b>{item.n}</b> : null}
                <span>{item.t}</span>
              </li>
            ))}
          </ul>

          <h4>What is left to you</h4>
          <ul className="left">
            {criticals ? (
              <li data-tone="bad">
                <b>{plural(criticals, 'critical issue')}</b> in the report. It cannot be filed until
                they are cleared.
              </li>
            ) : null}
            {short.map(sheet => (
              <li key={sheet.key} data-tone="bad">
                <b>{sheet.sheet}</b>
                {sheet.why ? ` — ${sheet.why}` : ' did not extract'}. Every field it feeds is empty
                in the report.
              </li>
            ))}
            {/* Only the workbook can put a missing fund in, so name it with what the parse said. */}
            {missedAifs.map(row => (
              <li key={row.fund_id || row.fund_name} data-tone="bad">
                <b>{row.fund_name}</b>
                {row.reason ? ` — ${row.reason}` : ' — the workbook could not be read for it'}. It
                is not in the report; add it to the workbook and run the report again.
              </li>
            ))}
            {missedManager ? (
              <li data-tone="bad">
                <b>The AIFM part</b>
                {missedManager.reason ? ` — ${missedManager.reason}` : ' did not convert'}.
              </li>
            ) : null}
            {warnings ? <li>{plural(warnings, 'warning')}, worth a read before filing.</li> : null}
            {!heldHealth ? (
              <li>The report’s own validation — its issue bar has the count.</li>
            ) : null}
            <li>Approval — nobody has signed this off yet.</li>
          </ul>
        </div>
      </Summary>

      <ApproverDrawer
        isOpen={approverOpen}
        onClose={() => setApproverOpen(false)}
        periodId={periodId}
        currentUser={currentUser}
        selectedPeriod={period}
      />
    </Modal>
  );
};

export default RunSummary;
